import { Check, LayoutGrid, PlusCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ContratacaoDashboardOption } from '@/api/contratacao.actions.schema';
import { CHECKOUT_MARKETING_CLASSES } from '@/lib/checkout-theme';

type Props = {
  open: boolean;
  loading: boolean;
  dashboards: ContratacaoDashboardOption[];
  blockedDashboards: Array<
    ContratacaoDashboardOption & {
      blockedReason: string;
    }
  >;
  selectedDashboardId: number | null;
  onSelectDashboard: (dashboardId: number | null) => void;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
};

export function DashboardTargetDialog({
  open,
  loading,
  dashboards,
  blockedDashboards,
  selectedDashboardId,
  onSelectDashboard,
  onConfirm,
  onOpenChange,
}: Props) {
  const hasDashboards = dashboards.length > 0;
  const hasBlockedDashboards = blockedDashboards.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl overflow-hidden border-slate-200 bg-gradient-to-b from-white to-slate-50/80 p-0">
        <DialogHeader className={cn('border-b border-slate-200 px-6 py-5', CHECKOUT_MARKETING_CLASSES.cardLightIndigo)}>
          <DialogTitle className="text-slate-900">Onde deseja contratar este plano?</DialogTitle>
          <DialogDescription className="text-slate-600">
            Escolha uma dashboard existente ou crie uma nova para esta contratação.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 max-h-[60vh] overflow-auto px-6 py-5">
          {loading ? (
            <p className="text-sm text-slate-500">Carregando dashboards...</p>
          ) : (
            <>
              {hasDashboards ? (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Dashboards que você pode contratar
                  </p>
                  {dashboards.map((dashboard) => {
                    const selected = selectedDashboardId === dashboard.id;
                    return (
                      <button
                        key={dashboard.id}
                        type="button"
                        onClick={() => onSelectDashboard(dashboard.id)}
                        className={cn(
                          'w-full rounded-xl border p-4 text-left transition-all duration-200',
                          selected
                            ? cn(
                                'border-indigo-300 shadow-sm',
                                CHECKOUT_MARKETING_CLASSES.cardLightIndigo,
                              )
                            : 'border-slate-200 bg-white hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-sm',
                        )}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 truncate">{dashboard.titulo}</p>
                            <p className="text-xs text-slate-600 mt-1">Dashboard existente</p>
                          </div>
                          <span
                            className={cn(
                              'inline-flex h-7 w-7 items-center justify-center rounded-full border',
                              selected
                                ? 'border-indigo-300 bg-indigo-100 text-indigo-700'
                                : 'border-slate-200 bg-white text-slate-400',
                            )}
                          >
                            {selected ? <Check className="h-4 w-4" /> : <LayoutGrid className="h-4 w-4" />}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </>
              ) : null}

              {hasBlockedDashboards ? (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mt-1">
                    Outras dashboards
                  </p>
                  {blockedDashboards.map((dashboard) => (
                    <div
                      key={dashboard.id}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 p-4 text-left opacity-90"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-800 truncate">{dashboard.titulo}</p>
                          <p className="text-xs text-slate-600 mt-1">{dashboard.blockedReason}</p>
                        </div>
                        <span
                          className={cn(
                            'inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400',
                          )}
                        >
                          <LayoutGrid className="h-4 w-4" />
                        </span>
                      </div>
                    </div>
                  ))}
                </>
              ) : null}

              <button
                type="button"
                onClick={() => onSelectDashboard(null)}
                className={cn(
                  'w-full rounded-xl border p-4 text-left transition-all duration-200',
                  selectedDashboardId === null
                    ? 'border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50 shadow-sm'
                    : 'border-slate-200 bg-white hover:-translate-y-0.5 hover:border-emerald-200 hover:shadow-sm',
                )}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">Quero uma nova dashboard</p>
                    <p className="text-xs text-slate-600 mt-1">
                      O plano será contratado para uma nova dashboard.
                    </p>
                  </div>
                  <span
                    className={cn(
                      'inline-flex h-7 w-7 items-center justify-center rounded-full border',
                      selectedDashboardId === null
                        ? 'border-emerald-300 bg-emerald-100 text-emerald-700'
                        : 'border-slate-200 bg-white text-slate-400',
                    )}
                  >
                    {selectedDashboardId === null ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <PlusCircle className="h-4 w-4" />
                    )}
                  </span>
                </div>
              </button>
            </>
          )}
        </div>

        <DialogFooter className="border-t border-slate-200 px-6 py-4">
          <Button type="button" className={CHECKOUT_MARKETING_CLASSES.accentBtnIndigo} onClick={onConfirm} disabled={loading}>
            Continuar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
